import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Users, MapPin, Shield, Settings2, FileText, ChevronUp, ChevronDown, AlertTriangle, Building2, MessageSquare } from "lucide-react";
import { useTranslation } from 'react-i18next';
import { toast } from "sonner";
import { format } from "date-fns";
import { api } from "../App";
import { LoadingOverlay } from "../components/LoadingOverlay";

// Import refactored dashboard components
import {
  DashboardHeader,
  DashboardStats,
  DashboardFilters,
  ApplicationsTable,
  Pagination,
  HierarchyBadge,
  HierarchyHelpModal,
  HobliApplicationsSummary,
} from "../components/dashboard";
import SLAAlertModal from "../components/dashboard/SLAAlertModal";

// Import existing components
import OfficialsManagement from "../components/OfficialsManagement";
import ApplicationsManagement from "../components/ApplicationsManagement";
import LocationBasedReports from "../components/LocationBasedReports";
import AuditLogs from "../components/AuditLogs";
import AnalyticsDashboard from "../components/AnalyticsDashboard";
import GOKDashboard from "../components/GOKDashboard";
import ImportApplications from "../components/ImportApplications";
import SLAViolationsPage from "../components/SLAViolationsPage";
import MessagingCentre from "../components/MessagingCentre";
import LanguageSwitcher from "../components/LanguageSwitcher";
// Location Management - Disabled for now, will enable in future
// import LocationManagement from "../components/LocationManagement";
import { Button } from "@/components/ui/button";
import { LogOut, Upload, BookOpen } from "lucide-react";
import ExportButtons from "../components/ExportButtons";
import NotificationBell from "../components/NotificationBell";
import StatusBadge from "../components/StatusBadge";
import WorkflowViewer from "../components/WorkflowViewer";
import MobileOfficialTabs from "../components/MobileOfficialTabs";

// Constants for super authorities and GOK officials
const SUPER_AUTHORITIES = ["tahsildar", "ddlr", "adlr"];
const GOK_DESIGNATIONS = ["dc", "ceo_zp"];
// Survey Department designations - Circle doesn't apply to these
const SURVEY_DESIGNATIONS = ['ddlr', 'adlr', 'surveyor', 'case_worker', 'superintendent', 'survey_supervisor'];

export default function OfficialDashboard({ user }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // RBAC: Check if user is from Survey Department (Circle doesn't apply to Survey)
  const isSurveyUser = user?.department === 'survey' || SURVEY_DESIGNATIONS.includes(user?.designation);
  const showCircleColumn = !isSurveyUser;
  
  // Parse initial values from URL params
  const getInitialFromParams = () => {
    return {
      status: searchParams.get("status") || "submitted",
      section: searchParams.get("section") || "all",
      circle: searchParams.get("circle") || "all",
      district: searchParams.get("district") || "RAMANAGARA",
      taluk: searchParams.get("taluk") || "MAGADI",
      hobli: searchParams.get("hobli") || "all",
      village: searchParams.get("village") || "all",
      search: searchParams.get("search") || "",
      page: parseInt(searchParams.get("page") || "1", 10),
      tab: searchParams.get("tab") || "applications",
    };
  };
  
  const initialParams = getInitialFromParams();
  
  // Core state
  const [applications, setApplications] = useState([]);
  const [filteredApps, setFilteredApps] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const tableLoadingRef = useRef(false);
  
  // Helper to set loading state immediately
  const setTableLoadingImmediate = (loading) => {
    tableLoadingRef.current = loading;
    setTableLoading(loading);
  };
  
  // Department and role state
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [isGOKOfficial, setIsGOKOfficial] = useState(false);
  const [isSuperAuthority, setIsSuperAuthority] = useState(false);
  
  // Filter state (initialized from URL params)
  const [statusFilter, setStatusFilter] = useState(initialParams.status);
  const [sectionFilter, setSectionFilter] = useState(initialParams.section);
  const [circleFilter, setCircleFilter] = useState(initialParams.circle);
  const [districtFilter, setDistrictFilter] = useState(initialParams.district);
  const [talukFilter, setTalukFilter] = useState(initialParams.taluk);
  const [hobliFilter, setHobliFilter] = useState(initialParams.hobli);
  const [villageFilter, setVillageFilter] = useState(initialParams.village);
  const [searchQuery, setSearchQuery] = useState(initialParams.search);
  const [debouncedSearch, setDebouncedSearch] = useState(initialParams.search);
  const searchInputRef = useRef(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(initialParams.page);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 50;
  
  // UI state
  const [showFilters, setShowFilters] = useState(false);
  const [activeTab, setActiveTab] = useState(initialParams.tab);
  const [showImport, setShowImport] = useState(false);
  const [showSLAModal, setShowSLAModal] = useState(false);
  const [controlsCollapsed, setControlsCollapsed] = useState(() => {
    // Load from localStorage, default to false (expanded)
    const saved = localStorage.getItem('dashboardControlsCollapsed');
    return saved === 'true';
  });
  
  // Individual section collapse states with localStorage persistence
  const [statsCollapsed, setStatsCollapsed] = useState(() => {
    const saved = localStorage.getItem('dashboardStatsCollapsed');
    return saved === 'true';
  });
  const [hobliCardCollapsed, setHobliCardCollapsed] = useState(() => {
    const saved = localStorage.getItem('dashboardHobliCardCollapsed');
    // Default to collapsed to give more space to applications table
    return saved !== null ? saved === 'true' : true;
  });
  const [filtersCollapsed, setFiltersCollapsed] = useState(() => {
    const saved = localStorage.getItem('dashboardFiltersCollapsed');
    // Default to collapsed to give more space to applications table
    return saved !== null ? saved === 'true' : true;
  });
  
  // Circle assignments for Village Accountant
  const [circleAssignments, setCircleAssignments] = useState(null);
  
  // Toggle controls and persist to localStorage
  const toggleControls = () => {
    const newState = !controlsCollapsed;
    setControlsCollapsed(newState);
    localStorage.setItem('dashboardControlsCollapsed', String(newState));
  };
  
  // Toggle stats section
  const toggleStats = () => {
    const newState = !statsCollapsed;
    setStatsCollapsed(newState);
    localStorage.setItem('dashboardStatsCollapsed', String(newState));
  };
  
  // Toggle hobli card section
  const toggleHobliCard = () => {
    const newState = !hobliCardCollapsed;
    setHobliCardCollapsed(newState);
    localStorage.setItem('dashboardHobliCardCollapsed', String(newState));
  };
  
  // Toggle filters section
  const toggleFiltersSection = () => {
    const newState = !filtersCollapsed;
    setFiltersCollapsed(newState);
    localStorage.setItem('dashboardFiltersCollapsed', String(newState));
  };
  
  // Handle tab change - instant, no loading state needed
  const handleTabChange = (newTab) => {
    if (newTab !== activeTab) {
      setActiveTab(newTab);
    }
  };
  
  // Available filter options (loaded from locations API)
  const [availableDistricts, setAvailableDistricts] = useState([]);
  const [availableTaluks, setAvailableTaluks] = useState([]);
  const [availableHoblis, setAvailableHoblis] = useState([]);
  const [availableVillages, setAvailableVillages] = useState([]);
  
  // Load locations from API on mount
  useEffect(() => {
    const loadLocations = async () => {
      try {
        // Fetch all districts (including disabled ones)
        const districtsRes = await api.get('/locations/districts?include_all=true');
        setAvailableDistricts(districtsRes.data);
        
        // Fetch taluks for default district
        const taluksRes = await api.get('/locations/taluks?district=RAMANAGARA&include_all=true');
        setAvailableTaluks(taluksRes.data);
        
        // Fetch hoblis for default taluk
        const hoblisRes = await api.get('/locations/hoblis?district=RAMANAGARA&taluk=MAGADI');
        setAvailableHoblis(hoblisRes.data);
      } catch (error) {
        console.error('Failed to load locations:', error);
      }
    };
    loadLocations();
  }, []);
  
  // Update taluks when district changes
  useEffect(() => {
    const loadTaluks = async () => {
      if (districtFilter && districtFilter !== 'all') {
        try {
          const taluksRes = await api.get(`/locations/taluks?district=${districtFilter.toUpperCase()}&include_all=true`);
          setAvailableTaluks(taluksRes.data);
        } catch (error) {
          console.error('Failed to load taluks:', error);
        }
      }
    };
    loadTaluks();
  }, [districtFilter]);
  
  // Update hoblis when taluk changes
  useEffect(() => {
    const loadHoblis = async () => {
      if (districtFilter && districtFilter !== 'all' && talukFilter && talukFilter !== 'all') {
        try {
          const hoblisRes = await api.get(`/locations/hoblis?district=${districtFilter.toUpperCase()}&taluk=${talukFilter.toUpperCase()}`);
          setAvailableHoblis(hoblisRes.data);
        } catch (error) {
          console.error('Failed to load hoblis:', error);
        }
      }
    };
    loadHoblis();
  }, [districtFilter, talukFilter]);
  
  // Update villages when hobli or circle changes
  useEffect(() => {
    const loadVillages = async () => {
      if (districtFilter && districtFilter !== 'all' && talukFilter && talukFilter !== 'all' && hobliFilter && hobliFilter !== 'all') {
        try {
          // If a specific circle is selected, get villages for that circle from applications
          if (circleFilter && circleFilter !== 'all') {
            const villagesRes = await api.get(`/locations/villages-by-circle?circle=${circleFilter}&hobli=${hobliFilter}`);
            setAvailableVillages(villagesRes.data);
          } else {
            // Otherwise get all villages for the hobli from locations
            const villagesRes = await api.get(`/locations/villages?district=${districtFilter.toUpperCase()}&taluk=${talukFilter.toUpperCase()}&hobli=${hobliFilter.toUpperCase()}`);
            setAvailableVillages(villagesRes.data);
          }
        } catch (error) {
          console.error('Failed to load villages:', error);
        }
      } else {
        setAvailableVillages([]);
      }
    };
    loadVillages();
  }, [districtFilter, talukFilter, hobliFilter, circleFilter]);
  
  // Set search input value from URL params on mount
  useEffect(() => {
    if (searchInputRef.current && initialParams.search) {
      searchInputRef.current.value = initialParams.search;
    }
  }, []);
  
  // Sync state to URL params
  useEffect(() => {
    const newParams = new URLSearchParams();
    if (statusFilter !== "submitted") newParams.set("status", statusFilter);
    if (sectionFilter !== "all") newParams.set("section", sectionFilter);
    if (circleFilter !== "all") newParams.set("circle", circleFilter);
    if (districtFilter !== "RAMANAGARA") newParams.set("district", districtFilter);
    if (talukFilter !== "MAGADI") newParams.set("taluk", talukFilter);
    if (hobliFilter !== "all") newParams.set("hobli", hobliFilter);
    if (villageFilter !== "all") newParams.set("village", villageFilter);
    if (searchQuery) newParams.set("search", searchQuery);
    if (currentPage > 1) newParams.set("page", currentPage.toString());
    if (activeTab !== "applications") newParams.set("tab", activeTab);
    
    setSearchParams(newParams, { replace: true });
  }, [statusFilter, sectionFilter, circleFilter, districtFilter, talukFilter, hobliFilter, villageFilter, searchQuery, currentPage, activeTab, setSearchParams]);
  
  // Check if user is a GOK official
  const isUserGOKOfficial = user?.designation && GOK_DESIGNATIONS.includes(user.designation);

  // Initialize department and role on mount
  useEffect(() => {
    const dept = localStorage.getItem("selectedDepartment");
    if (dept) {
      setSelectedDepartment(dept);
    }
    
    if (user?.designation && GOK_DESIGNATIONS.includes(user.designation)) {
      setIsGOKOfficial(true);
      setIsSuperAuthority(true);
      setSelectedDepartment("all");
    } else if (user?.designation && SUPER_AUTHORITIES.includes(user.designation)) {
      setIsSuperAuthority(true);
    } else if (user?.designation) {
      // Non-super authority officials: default to "assigned" status if no URL param
      if (!searchParams.get("status")) {
        setStatusFilter("assigned");
      }
    }
  }, [user, searchParams]);

  // Fetch circle assignments for Village Accountants
  useEffect(() => {
    const fetchCircleAssignments = async () => {
      if (user?.designation === 'village_accountant') {
        try {
          const response = await api.get('/official/circle-assignments');
          setCircleAssignments(response.data);
        } catch (error) {
          console.error('Failed to fetch circle assignments:', error);
        }
      }
    };
    fetchCircleAssignments();
  }, [user]);

  // Show SLA modal for super authorities immediately on mount
  useEffect(() => {
    if (isSuperAuthority) {
      // Don't show SLA modal if password change is required
      const mustChangePassword = localStorage.getItem('mustChangePassword');
      if (mustChangePassword === 'true') {
        return;
      }
      
      // Check if we've already shown the modal in this session
      const modalShownKey = `sla_modal_shown_${new Date().toDateString()}`;
      const alreadyShown = sessionStorage.getItem(modalShownKey);
      
      if (!alreadyShown) {
        setShowSLAModal(true);
        sessionStorage.setItem(modalShownKey, 'true');
      }
    }
  }, [isSuperAuthority]);

  // Track if this is the initial load
  const isInitialLoadRef = useRef(true);

  // Load data when filters change
  useEffect(() => {
    if ((selectedDepartment || isGOKOfficial) && !isUserGOKOfficial) {
      loadData(isInitialLoadRef.current);
      isInitialLoadRef.current = false;
    }
  }, [selectedDepartment, departmentFilter, sectionFilter, circleFilter, districtFilter, talukFilter, hobliFilter, villageFilter, statusFilter, debouncedSearch, currentPage, isGOKOfficial, isUserGOKOfficial]);
  
  // Reset to page 1 when filters change (but not on initial load)
  const isFilterChangeRef = useRef(false);
  const prevFiltersRef = useRef({
    statusFilter,
    sectionFilter,
    circleFilter,
    districtFilter,
    talukFilter,
    hobliFilter,
    villageFilter,
    debouncedSearch
  });
  
  useEffect(() => {
    const prevFilters = prevFiltersRef.current;
    const filtersChanged = 
      prevFilters.statusFilter !== statusFilter ||
      prevFilters.sectionFilter !== sectionFilter ||
      prevFilters.circleFilter !== circleFilter ||
      prevFilters.districtFilter !== districtFilter ||
      prevFilters.talukFilter !== talukFilter ||
      prevFilters.hobliFilter !== hobliFilter ||
      prevFilters.villageFilter !== villageFilter ||
      prevFilters.debouncedSearch !== debouncedSearch;
    
    if (isFilterChangeRef.current && filtersChanged) {
      setCurrentPage(1);
    }
    
    isFilterChangeRef.current = true;
    prevFiltersRef.current = {
      statusFilter,
      sectionFilter,
      circleFilter,
      districtFilter,
      talukFilter,
      hobliFilter,
      villageFilter,
      debouncedSearch
    };
  }, [statusFilter, sectionFilter, circleFilter, districtFilter, talukFilter, hobliFilter, villageFilter, debouncedSearch]);

  // Update filtered apps when applications change
  useEffect(() => {
    if (!isUserGOKOfficial) {
      setFilteredApps(applications);
    }
  }, [applications, isUserGOKOfficial]);

  // Delayed spinner timer ref
  const spinnerTimeoutRef = useRef(null);
  const SPINNER_DELAY_MS = 300; // Only show spinner if request takes > 300ms

  // Load data from API with delayed spinner
  const loadData = async (isInitialLoad = false) => {
    // Clear any existing spinner timeout
    if (spinnerTimeoutRef.current) {
      clearTimeout(spinnerTimeoutRef.current);
      spinnerTimeoutRef.current = null;
    }

    if (isInitialLoad) {
      setLoading(true);
    } else {
      // Delayed spinner: only show if request takes longer than SPINNER_DELAY_MS
      spinnerTimeoutRef.current = setTimeout(() => {
        tableLoadingRef.current = true;
        setTableLoading(true);
      }, SPINNER_DELAY_MS);
    }
    
    try {
      const params = new URLSearchParams();
      
      params.append("page", currentPage.toString());
      params.append("limit", pageSize.toString());
      
      if (isGOKOfficial) {
        if (departmentFilter !== "all") {
          params.append("department", departmentFilter);
        }
      } else if (selectedDepartment) {
        params.append("department", selectedDepartment);
      }
      
      if (sectionFilter !== "all") params.append("section", sectionFilter);
      if (circleFilter !== "all") params.append("circle", circleFilter);
      if (districtFilter !== "all") params.append("district", districtFilter);
      if (talukFilter !== "all") params.append("taluk", talukFilter);
      if (hobliFilter !== "all") params.append("hobli", hobliFilter);
      if (villageFilter !== "all") params.append("village", villageFilter);
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (debouncedSearch && debouncedSearch.trim()) {
        params.append("search", debouncedSearch.trim());
      }
      
      const queryString = params.toString() ? `?${params.toString()}` : "";
      
      const [appsRes, statsRes] = await Promise.all([
        api.get(`/applications${queryString}`),
        api.get(`/dashboard/stats${queryString}`),
      ]);
      
      setApplications(appsRes.data);
      setStats(statsRes.data);
      
      const totalCountHeader = appsRes.headers['x-total-count'];
      const totalPagesHeader = appsRes.headers['x-total-pages'];
      if (totalCountHeader) setTotalCount(parseInt(totalCountHeader, 10));
      if (totalPagesHeader) setTotalPages(parseInt(totalPagesHeader, 10));
    } catch (error) {
      toast.error(t('common.failedToLoad'));
    } finally {
      // Clear the spinner timeout if it hasn't fired yet
      if (spinnerTimeoutRef.current) {
        clearTimeout(spinnerTimeoutRef.current);
        spinnerTimeoutRef.current = null;
      }
      // Remove dashboard-loading class if it was added
      document.body.classList.remove('dashboard-loading');
      setLoading(false);
      tableLoadingRef.current = false;
      setTableLoading(false);
    }
  };

  // Search handlers - no immediate spinner, let delayed spinner handle it
  const handleSearch = () => {
    const value = searchInputRef.current?.value || "";
    tableLoadingRef.current = true;
    setTableLoading(true);
    setSearchQuery(value);
    setDebouncedSearch(value);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    if (searchInputRef.current) {
      searchInputRef.current.value = '';
    }
    tableLoadingRef.current = true;
    setTableLoading(true);
    setSearchQuery("");
    setDebouncedSearch("");
    setCurrentPage(1);
  };

  // Clear all filters - no immediate spinner, let delayed spinner handle it
  const clearAllFilters = () => {
    setStatusFilter("submitted");
    setSectionFilter("all");
    setCircleFilter("all");
    setDistrictFilter("RAMANAGARA");
    setTalukFilter("MAGADI");
    setHobliFilter("all");
    setVillageFilter("all");
    setSearchQuery("");
    setDebouncedSearch("");
    setCurrentPage(1);
    if (searchInputRef.current) {
      searchInputRef.current.value = '';
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    // Clear SLA modal session flag so it shows again on next login
    const modalShownKey = `sla_modal_shown_${new Date().toDateString()}`;
    sessionStorage.removeItem(modalShownKey);
    navigate("/login");
  };

  // Check if user can import applications (Tahsildar, DDLR, or ADLR)
  const canImport = ["tahsildar", "ddlr", "adlr"].includes(user?.designation);

  // Render GOK Dashboard for GOK officials
  if (isUserGOKOfficial) {
    return <GOKDashboard user={user} />;
  }

  // Render Import Applications view
  if (showImport) {
    return (
      <div className="min-h-screen bg-stone-100 pb-16 md:pb-0">
        <header className="bg-white border-b border-stone-200 sticky top-0 z-50 shadow-sm">
          <div className="w-full px-4 sm:px-6 py-3 sm:py-4">
            <div className="flex justify-between items-center">
              <div className="min-w-0 flex-1">
                <h1 className="text-lg sm:text-2xl font-manrope font-bold text-emerald-800 tracking-tight truncate">
                  {t('appName')}
                </h1>
                <p className="text-xs sm:text-sm text-slate-500 truncate">
                  {t('dashboard.welcome')}, {user?.name}
                </p>
              </div>
              <div className="flex items-center gap-1 sm:gap-2 ml-2">
                <LanguageSwitcher />
                <Button
                  variant="outline"
                  onClick={() => navigate("/help")}
                  className="border-emerald-500 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 hover:text-emerald-800 px-2 sm:px-4 font-semibold"
                  data-testid="help-button"
                >
                  <BookOpen className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">{t('common.help') || 'Help'}</span>
                </Button>
                <Button
                  variant="ghost"
                  onClick={handleLogout}
                  className="hover:bg-stone-100 text-slate-600 px-2 sm:px-4"
                  data-testid="logout-button"
                >
                  <LogOut className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">{t('dashboard.logout')}</span>
                </Button>
              </div>
            </div>
          </div>
        </header>
        <main className="w-full px-4 sm:px-6 py-4 sm:py-8">
          <ImportApplications 
            onBack={() => {
              setShowImport(false);
              loadData();
            }}
            user={user}
          />
        </main>
      </div>
    );
  }

  if (loading) {
    return <LoadingOverlay />;
  }

  return (
    <div className="min-h-screen bg-stone-100 pb-16 md:pb-0">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-50 shadow-sm">
        <div className="w-full px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex justify-between items-center">
            <div className="min-w-0 flex-1">
              <h1 className="text-lg sm:text-2xl font-manrope font-bold text-emerald-800 tracking-tight truncate">
                {t('appName')}
              </h1>
              <p className="text-xs sm:text-sm text-slate-500 truncate">
                {t('dashboard.welcome')}, {user?.name} 
                {user?.designation && (
                  <span className="font-bold text-emerald-700"> ({t(`designation.${user.designation}`)})</span>
                )}
              </p>
              
              {/* Hierarchy Level Indicator - hidden on mobile PWA */}
              {user?.designation && (
                <div className="mt-1.5 hidden md:flex items-center gap-2" data-testid="hierarchy-indicator">
                  <HierarchyBadge user={user} />
                  <HierarchyHelpModal />
                  {/* Workflow Viewer Button - Only for Super Authorities */}
                  {isSuperAuthority && (
                    <WorkflowViewer user={user} />
                  )}
                </div>
              )}
              
              {/* Circle Assignments for Village Accountant */}
              {user?.designation === 'village_accountant' && circleAssignments && circleAssignments.has_assignments && (
                <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="va-circle-assignments">
                  <span className="text-xs text-slate-500 flex items-center gap-1">
                    <Building2 className="w-3 h-3" />
                    {t('dashboard.assignedCircles') || 'Assigned Circles'}:
                  </span>
                  {circleAssignments.circle_assignments.map((assignment, index) => {
                    const circleVal = typeof assignment === 'string' ? assignment : (assignment?.circle || '');
                    const hobliVal = typeof assignment === 'string' ? '' : (assignment?.hobli || '');
                    const villageCount = typeof assignment === 'object' ? (assignment?.village_count || 0) : 0;
                    return (
                      <span 
                        key={`${hobliVal}-${circleVal}-${index}`}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-emerald-100 text-emerald-800 border border-emerald-200"
                        data-testid={`circle-badge-${index}`}
                      >
                        <MapPin className="w-3 h-3" />
                        <span className="font-medium">{circleVal}</span>
                        {villageCount > 0 && (
                          <span className="text-emerald-600">
                            ({villageCount} {t('dashboard.villages') || 'villages'})
                          </span>
                        )}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="flex items-center gap-1 sm:gap-2 ml-2">
              {canImport && (
                <Button
                  variant="outline"
                  onClick={() => setShowImport(true)}
                  className="hidden sm:flex items-center gap-2 text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                  data-testid="import-applications-btn"
                >
                  <Upload className="w-4 h-4" />
                  {t('import.title')}
                </Button>
              )}
              <div className="hidden sm:block">
                <ExportButtons userRole={user?.role} userDesignation={user?.designation} />
              </div>
              <LanguageSwitcher />
              <NotificationBell />
              <Button
                variant="outline"
                onClick={() => navigate("/help")}
                className="border-emerald-500 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 hover:text-emerald-800 px-2 sm:px-4 font-semibold"
                data-testid="help-button"
              >
                <BookOpen className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">{t('common.help') || 'Help'}</span>
              </Button>
              <Button
                variant="ghost"
                onClick={handleLogout}
                className="hover:bg-stone-100 text-slate-600 px-2 sm:px-4"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">{t('dashboard.logout')}</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="w-full px-4 sm:px-6 py-4 sm:py-8">
        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full" data-testid="dashboard-tabs">
          {/* Sticky Section - Tabs + Stats + Filters + Pagination + Table Header */}
          {/* Disable sticky when controls expanded AND more filters shown (takes too much space) */}
          <div className={`${(!controlsCollapsed && showFilters) ? '' : 'sticky top-[73px]'} z-40 bg-stone-100 space-y-4 pb-0`}>
            {/* Mobile: Active tab indicator + Toggle Controls inline */}
            <div className="md:hidden flex items-center justify-between py-1">
              <span className="text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1" data-testid="mobile-active-tab-label">
                {activeTab === "applications" && (t('analytics.applicationsTab') || "Applications")}
                {activeTab === "officials" && (t('officialsManagement.title') || "Officials")}
                {activeTab === "applications-management" && (t('applicationsManagement.tabTitle') || "Applications Management")}
                {activeTab === "location-reports" && (t('locationReports.tabTitle') || "Location Reports")}
                {activeTab === "sla-violations" && (t('sla.tabTitle') || "SLA Issues")}
                {activeTab === "analytics" && (t('analytics.analyticsTab') || "Analytics")}
                {activeTab === "audit-logs" && (t('auditLogs.tabTitle') || "Audit Logs")}
                {activeTab === "messaging" && (t('messaging.tabTitle') || "Messaging Centre")}
              </span>
              {activeTab === "applications" && (
                <button
                  onClick={toggleControls}
                  className="flex items-center gap-0.5 text-[10px] font-medium rounded-full px-2 py-0.5 border transition-colors bg-gray-800 text-gray-200 border-gray-700 active:bg-gray-700"
                  data-testid="toggle-controls-btn-mobile"
                >
                  {controlsCollapsed ? <ChevronDown className="w-2.5 h-2.5" /> : <ChevronUp className="w-2.5 h-2.5" />}
                  {controlsCollapsed ? t('dashboard.showControls') : t('dashboard.hideControls')}
                </button>
              )}
            </div>
            {/* Tab Navigation - Desktop only */}
            <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 hidden md:flex items-center">
              <TabsList className="bg-emerald-50 p-1 inline-flex min-w-max sm:flex sm:flex-wrap gap-1">
                <TabsTrigger 
                  value="applications" 
                  data-testid="applications-tab"
                  className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white hover:bg-emerald-100 text-emerald-800 text-xs sm:text-sm whitespace-nowrap"
                >
                  {t('analytics.applicationsTab')}
                </TabsTrigger>
                {isSuperAuthority && (
                  <TabsTrigger 
                    value="officials" 
                    data-testid="officials-tab"
                    className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white hover:bg-emerald-100 text-emerald-800 text-xs sm:text-sm whitespace-nowrap"
                  >
                    <Users className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">{t('officialsManagement.title')}</span>
                    <span className="sm:hidden">{t('tabs.officialsShort')}</span>
                  </TabsTrigger>
                )}
                {isSuperAuthority && (
                  <TabsTrigger 
                    value="applications-management" 
                    data-testid="applications-management-tab"
                    className="data-[state=active]:bg-amber-600 data-[state=active]:text-white hover:bg-amber-100 text-amber-800 text-xs sm:text-sm whitespace-nowrap"
                  >
                    <FileText className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">{t('applicationsManagement.tabTitle')}</span>
                    <span className="sm:hidden">{t('tabs.appsShort') || 'ಅರ್ಜಿ'}</span>
                  </TabsTrigger>
                )}
                {isSuperAuthority && (
                  <TabsTrigger 
                    value="location-reports" 
                    data-testid="location-reports-tab"
                    className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white hover:bg-emerald-100 text-emerald-800 text-xs sm:text-sm whitespace-nowrap"
                  >
                    <MapPin className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">{t('locationReports.tabTitle')}</span>
                    <span className="sm:hidden">{t('tabs.locationShort')}</span>
                  </TabsTrigger>
                )}
                {!isGOKOfficial && (
                  <TabsTrigger 
                    value="sla-violations" 
                    data-testid="sla-violations-tab"
                    className="data-[state=active]:bg-blue-600 data-[state=active]:text-white hover:bg-blue-100 text-blue-700 text-xs sm:text-sm whitespace-nowrap"
                  >
                    <AlertTriangle className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">{t('sla.tabTitle') || 'SLA Issues'}</span>
                    <span className="sm:hidden">{t('tabs.slaShort') || 'SLA'}</span>
                  </TabsTrigger>
                )}
                {isSuperAuthority && (
                  <TabsTrigger 
                    value="messaging" 
                    data-testid="messaging-tab"
                    className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white hover:bg-emerald-100 text-emerald-800 text-xs sm:text-sm whitespace-nowrap"
                  >
                    <MessageSquare className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">{t('messaging.tabTitle') || 'Messaging Centre'}</span>
                    <span className="sm:hidden">{t('messaging.tabShort') || 'Messages'}</span>
                  </TabsTrigger>
                )}
                <TabsTrigger 
                  value="analytics" 
                  data-testid="analytics-tab"
                  className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white hover:bg-emerald-100 text-emerald-800 text-xs sm:text-sm whitespace-nowrap"
                >
                  {t('analytics.analyticsTab')}
                </TabsTrigger>
                {isSuperAuthority && (
                  <TabsTrigger 
                    value="audit-logs" 
                    data-testid="audit-logs-tab"
                    className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white hover:bg-emerald-100 text-emerald-800 text-xs sm:text-sm whitespace-nowrap"
                  >
                    <Shield className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">{t('auditLogs.tabTitle') || 'Audit Logs'}</span>
                    <span className="sm:hidden">{t('tabs.auditShort')}</span>
                  </TabsTrigger>
                )}
                {/* Location Management Tab - Disabled for now, will enable in future
                {canImport && (
                  <TabsTrigger 
                    value="location-management" 
                    data-testid="location-management-tab"
                    className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white hover:bg-emerald-100 text-emerald-800 text-xs sm:text-sm whitespace-nowrap"
                  >
                    <Settings2 className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">{t('locationManagement.title')}</span>
                    <span className="sm:hidden">{t('tabs.locationsShort')}</span>
                  </TabsTrigger>
                )}
                */}
              </TabsList>
              
              {/* Right side buttons container - desktop only, positioned to the right */}
              <div className="ml-auto flex items-center gap-2">
                {/* Toggle Controls Button - Only for Applications tab */}
                {activeTab === "applications" && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={toggleControls}
                    className="flex items-center gap-1 text-xs font-medium animate-pulse bg-gray-900 text-white border-gray-900 hover:!bg-gray-700 hover:!text-white hover:!border-gray-700 transition-colors"
                    data-testid="toggle-controls-btn"
                  >
                    {controlsCollapsed ? (
                      <>
                        <ChevronDown className="w-4 h-4" />
                        <span>{t('dashboard.showControls')}</span>
                      </>
                    ) : (
                      <>
                        <ChevronUp className="w-4 h-4" />
                        <span>{t('dashboard.hideControls')}</span>
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>

            {/* Stats, Filters, Pagination, Table Header - Only for Applications tab */}
            {activeTab === "applications" && (
              <>
                {/* Collapsed: Show only Pagination */}
                {controlsCollapsed && (
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalCount={totalCount}
                    pageSize={pageSize}
                    onPageChange={setCurrentPage}
                    isLoading={tableLoading}
                    position="top"
                  />
                )}

                {/* Expandable Controls Section */}
                {!controlsCollapsed && (
                  <>
                    {/* Stats Cards Section - Collapsible */}
                    <div className="mb-4">
                      <button
                        onClick={toggleStats}
                        className="flex items-center gap-2 text-sm text-red-600 hover:text-red-700 mb-2 transition-colors"
                        data-testid="toggle-stats-btn"
                      >
                        {statsCollapsed ? (
                          <ChevronDown className="w-4 h-4" />
                        ) : (
                          <ChevronUp className="w-4 h-4" />
                        )}
                        <span className="font-medium">{t('dashboard.applicationStats') || 'Applications Statistics'}</span>
                      </button>
                      {!statsCollapsed && <DashboardStats stats={stats} />}
                    </div>
                    
                    {/* Hobli Applications Summary - Collapsible */}
                    {isSuperAuthority && (
                      <div className="mb-4">
                        <button
                          onClick={toggleHobliCard}
                          className="flex items-center gap-2 text-sm text-red-600 hover:text-red-700 mb-2 transition-colors"
                          data-testid="toggle-hobli-card-btn"
                        >
                          {hobliCardCollapsed ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronUp className="w-4 h-4" />
                          )}
                          <MapPin className="w-4 h-4 text-red-500" />
                          <span className="font-medium">{t('dashboard.applicationsPerHobli') || 'Applications per Hobli'}</span>
                        </button>
                        {!hobliCardCollapsed && (
                          <HobliApplicationsSummary 
                            department={departmentFilter !== "all" ? departmentFilter : selectedDepartment}
                            onHobliClick={(hobli) => {
                              // Show loading spinner immediately
                              tableLoadingRef.current = true;
                              setTableLoading(true);
                              setHobliFilter(hobli);
                            }}
                          />
                        )}
                      </div>
                    )}

                    {/* Filters Bar — compact row + modal */}
                    <DashboardFilters
                      searchInputRef={searchInputRef}
                      onSearch={handleSearch}
                      onClearSearch={handleClearSearch}
                      searchQuery={searchQuery}
                      statusFilter={statusFilter}
                      onStatusChange={setStatusFilter}
                      sectionFilter={sectionFilter}
                      onSectionChange={setSectionFilter}
                      circleFilter={circleFilter}
                      onCircleChange={setCircleFilter}
                      districtFilter={districtFilter}
                      onDistrictChange={setDistrictFilter}
                      talukFilter={talukFilter}
                      onTalukChange={setTalukFilter}
                      hobliFilter={hobliFilter}
                      onHobliChange={setHobliFilter}
                      villageFilter={villageFilter}
                      onVillageChange={setVillageFilter}
                      showFilters={showFilters}
                      onToggleFilters={() => setShowFilters(!showFilters)}
                      availableDistricts={availableDistricts}
                      availableTaluks={availableTaluks}
                      availableHoblis={availableHoblis}
                      availableVillages={availableVillages}
                      onClearAllFilters={clearAllFilters}
                      onTableLoading={setTableLoading}
                      user={user}
                    />

                    {/* Top Pagination */}
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalCount={totalCount}
                      pageSize={pageSize}
                      onPageChange={setCurrentPage}
                      isLoading={tableLoading}
                      position="top"
                    />
                  </>
                )}

                {/* Table Header - Part of Sticky Section */}
                <div className="bg-white rounded-t-lg border border-b-0 border-stone-200 shadow-sm overflow-hidden">
                  <div className="hidden md:block">
                    <table className="w-full table-fixed" data-testid="applications-table-header">
                      <colgroup>
                        <col className="w-[60px]" />
                        <col className="w-[120px]" />
                        <col className="w-[150px]" />
                        <col className="w-[180px]" />
                        <col className="w-[150px]" />
                        <col className="w-[100px]" />
                        {showCircleColumn && <col className="w-[100px]" />}
                        <col className="w-[110px]" />
                        <col className="w-[100px]" />
                        <col className="w-[100px]" />
                      </colgroup>
                      <thead className="bg-stone-50 border-b border-stone-200">
                        <tr>
                          <th className="px-3 py-3 text-left text-xs font-semibold text-emerald-700 uppercase tracking-wider">
                            {t('table.applicationId')}
                          </th>
                          <th className="px-3 py-3 text-center text-xs font-semibold text-emerald-700 uppercase tracking-wider pl-6">
                            {t('table.surveyNumber')}
                          </th>
                          <th className="px-3 py-3 text-center text-xs font-semibold text-emerald-700 uppercase tracking-wider">
                            {t('table.farmer')}
                          </th>
                          <th className="px-3 py-3 text-center text-xs font-semibold text-emerald-700 uppercase tracking-wider">
                            {t('table.purpose')}
                          </th>
                          <th className="px-3 py-3 text-center text-xs font-semibold text-emerald-700 uppercase tracking-wider">
                            {t('table.location')}
                          </th>
                          <th className="px-3 py-3 text-center text-xs font-semibold text-emerald-700 uppercase tracking-wider">
                            {t('section.title')}
                          </th>
                          {showCircleColumn && (
                            <th className="px-3 py-3 text-center text-xs font-semibold text-emerald-700 uppercase tracking-wider">
                              {t('circle.title')}
                            </th>
                          )}
                          <th className="px-3 py-3 text-center text-xs font-semibold text-emerald-700 uppercase tracking-wider">
                            {t('table.status')}
                          </th>
                          <th className="px-3 py-3 text-center text-xs font-semibold text-emerald-700 uppercase tracking-wider">
                            {t('table.date')}
                          </th>
                          <th className="px-3 py-3 text-center text-xs font-semibold text-emerald-700 uppercase tracking-wider">
                            {t('table.action')}
                          </th>
                        </tr>
                      </thead>
                    </table>
                  </div>
                  {/* Mobile Header */}
                  <div className="md:hidden px-4 py-3 bg-stone-50 border-b border-stone-200">
                    <span className="text-xs font-semibold text-emerald-700 uppercase">{t('analytics.applicationsTab')}</span>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Applications Tab Content - Scrollable Body */}
          <TabsContent value="applications" className="mt-0">
            {/* Loading Overlay */}
            {tableLoading && <LoadingOverlay />}

            {/* Scrollable Table Body */}
            <div className={`bg-white rounded-b-lg border border-t-0 border-stone-200 shadow-sm overflow-hidden transition-opacity duration-150 ${tableLoading ? 'opacity-60' : 'opacity-100'}`}>
              {filteredApps.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">{t('table.noApplicationsFound')}</p>
                </div>
              ) : (
                <>
                  {/* Desktop: Table Body */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full table-fixed">
                      <colgroup>
                        <col className="w-[90px]" />
                        <col className="w-[100px]" />
                        <col className="w-[150px]" />
                        <col className="w-[180px]" />
                        <col className="w-[150px]" />
                        <col className="w-[100px]" />
                        {showCircleColumn && <col className="w-[100px]" />}
                        <col className="w-[110px]" />
                        <col className="w-[100px]" />
                        <col className="w-[100px]" />
                      </colgroup>
                      <tbody className="divide-y divide-stone-200">
                        {filteredApps.map((app, index) => (
                          <tr
                            key={app.id}
                            className="hover:bg-stone-50 transition-colors cursor-pointer"
                            onClick={() => navigate(`/application/${app.id}`)}
                            data-testid={`application-row-${app.id}`}
                          >
                            <td className="px-3 py-4 whitespace-nowrap text-left">
                              {app.application_id ? (
                                <div>
                                  <span className="text-xs font-semibold text-blue-600 font-mono block">{app.application_id.substring(0, 7)}</span>
                                  <span className="text-sm font-bold text-blue-700 font-mono">{app.application_id.substring(8)}</span>
                                </div>
                              ) : (
                                <span className="text-sm text-slate-400">-</span>
                              )}
                            </td>
                            <td className="px-3 py-4 text-left pl-6">
                              <span className="font-mono text-sm text-slate-900 break-words">{app.land_survey_number}</span>
                            </td>
                            <td className="px-3 py-4 overflow-hidden text-center">
                              <div>
                                <p className="text-sm font-medium text-slate-900 break-words">{app.farmer_name}</p>
                                <p className="text-xs text-slate-500 truncate">{app.farmer_email}</p>
                              </div>
                            </td>
                            <td className="px-3 py-4 overflow-hidden text-center">
                              <span className="text-sm text-slate-900 line-clamp-2">{app.purpose}</span>
                            </td>
                            <td className="px-3 py-4 overflow-hidden text-center">
                              <div className="text-sm text-slate-900 truncate">
                                <span>{app.village}</span>
                                {app.hobli && <span className="text-slate-500">, {app.hobli}</span>}
                              </div>
                              <div className="text-xs text-slate-500 truncate">
                                {app.taluk && <span>{app.taluk}, </span>}
                                {app.district}
                              </div>
                            </td>
                            <td className="px-3 py-4 whitespace-nowrap overflow-hidden text-center">
                              {app.section ? (
                                <span className="text-sm font-medium text-emerald-700 truncate">
                                  {t(`section.${app.section}`) || app.section}
                                </span>
                              ) : (
                                <span className="text-xs text-slate-400">{t('section.notAssigned')}</span>
                              )}
                            </td>
                            {showCircleColumn && (
                              <td className="px-3 py-4 text-center">
                                {app.circle ? (
                                  <span className="text-sm text-slate-700 break-words">
                                    {(() => {
                                      const translated = t(`circle.${app.circle}`);
                                      // If translation returns the key or starts with 'circle.', use original value
                                      return (translated === `circle.${app.circle}` || translated.startsWith('circle.')) 
                                        ? app.circle.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
                                        : translated;
                                    })()}
                                  </span>
                                ) : (
                                  <span className="text-xs text-slate-400">-</span>
                                )}
                              </td>
                            )}
                            <td className="px-3 py-4 whitespace-nowrap text-center">
                              <StatusBadge status={app.status} />
                            </td>
                            <td className="px-3 py-4 whitespace-nowrap text-center">
                              <span className="text-sm text-slate-500">{format(new Date(app.created_at), "MMM dd")}</span>
                            </td>
                            <td className="px-3 py-4 whitespace-nowrap text-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/application/${app.id}`);
                                }}
                                className="text-emerald-800 hover:text-emerald-900 hover:bg-emerald-50 text-xs px-2"
                                data-testid={`view-application-${app.id}`}
                              >
                                {t('table.viewDetails')}
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile: Card View */}
                  <div className="md:hidden divide-y divide-stone-200">
                    {filteredApps.map((app) => (
                      <div
                        key={app.id}
                        className="p-4 hover:bg-stone-50 transition-colors"
                        onClick={() => navigate(`/application/${app.id}`)}
                        data-testid={`application-card-${app.id}`}
                      >
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <p className="font-mono text-sm font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded inline-block">{app.application_id || '-'}</p>
                            <p className="text-sm font-medium text-slate-900 mt-1">{app.farmer_name}</p>
                            <p className="font-mono text-xs text-slate-500">{t('table.surveyNumber')}: {app.land_survey_number}</p>
                          </div>
                          <StatusBadge status={app.status} />
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                          <div>
                            <p className="text-xs text-slate-500">{t('table.purpose')}</p>
                            <p className="text-slate-900 truncate">{app.purpose}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">{t('table.landArea')}</p>
                            <p className="text-slate-900">{app.land_area} {t('application.acres')}</p>
                          </div>
                          <div className="col-span-2">
                            <p className="text-xs text-slate-500">{t('table.location')}</p>
                            <p className="text-slate-900">
                              {app.village}{app.hobli && `, ${app.hobli}`}
                              {app.taluk && `, ${app.taluk}`}, {app.district}
                            </p>
                          </div>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-slate-500">
                            {format(new Date(app.created_at), "MMM dd, yyyy")}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/application/${app.id}`);
                            }}
                            className="text-emerald-800 border-emerald-200 hover:bg-emerald-50"
                            data-testid={`view-application-mobile-${app.id}`}
                          >
                            {t('table.viewDetails')}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Bottom Pagination */}
            <div className="mt-4">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={totalCount}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                isLoading={tableLoading}
                position="bottom"
              />
            </div>
          </TabsContent>

          {/* Analytics Tab Content */}
          <TabsContent value="analytics">
            <AnalyticsDashboard />
          </TabsContent>

          {/* Officials Management Tab */}
          {isSuperAuthority && (
            <TabsContent value="officials">
              <OfficialsManagement user={user} />
            </TabsContent>
          )}

          {/* Applications Management Tab */}
          {isSuperAuthority && (
            <TabsContent value="applications-management">
              <ApplicationsManagement user={user} />
            </TabsContent>
          )}

          {/* Location Reports Tab */}
          {isSuperAuthority && (
            <TabsContent value="location-reports">
              <LocationBasedReports user={user} activeTab={activeTab} />
            </TabsContent>
          )}

          {/* SLA Violations Tab - Available for all officials */}
          {!isGOKOfficial && (
            <TabsContent value="sla-violations">
              <SLAViolationsPage user={user} />
            </TabsContent>
          )}

          {/* Audit Logs Tab */}
          {isSuperAuthority && (
            <TabsContent value="audit-logs">
              <AuditLogs />
            </TabsContent>
          )}

          {/* Messaging Centre Tab */}
          {isSuperAuthority && (
            <TabsContent value="messaging">
              <MessagingCentre user={user} />
            </TabsContent>
          )}

          {/* Location Management Tab - Disabled for now, will enable in future
          {canImport && (
            <TabsContent value="location-management">
              <LocationManagement user={user} />
            </TabsContent>
          )}
          */}
        </Tabs>
      </main>

      {/* SLA Alert Modal - Shows on login for Super Authorities */}
      {isSuperAuthority && (
        <SLAAlertModal
          isOpen={showSLAModal}
          onClose={() => setShowSLAModal(false)}
          onViewViolations={() => setActiveTab("sla-violations")}
          user={user}
        />
      )}

      {/* Mobile Bottom Tab Bar for Officials */}
      <MobileOfficialTabs
        activeTab={activeTab}
        onTabChange={handleTabChange}
        isSuperAuthority={isSuperAuthority}
        isGOKOfficial={isGOKOfficial}
      />
    </div>
  );
}
